import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { createCommunication, updateCommunication, createCommunicationType, suggestCommContacts } from "../../lib/api";
import { TYPE_LABELS, ROLE_OPTIONS } from "./constants";
import { nowLocalDateTimeStr } from "../../lib/date";

const todayIso = () => nowLocalDateTimeStr();

const blank = {
  type: "text",
  direction: "outgoing",
  contact_name: "",
  contact_role: "parent",
  contact_phone: "",
  contact_email: "",
  student_id: "",
  subject: "",
  content: "",
  visibility: "only_me",
  occurred_at: "",
  follow_up_date: "",
  follow_up_reminder_days: 1,
  follow_up_notes: "",
  tasks: [],
};

export default function CommunicationDialog({ open, onOpenChange, editing, staff, students, customTypes, onSaved, defaultStudentId = "" }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...blank,
        ...editing,
        student_id: editing.student_id || "",
        tasks: (editing.tasks || []).map((t) => ({ ...t })),
        occurred_at: editing.occurred_at ? editing.occurred_at.slice(0, 16) : "",
      });
    } else {
      setForm({ ...blank, student_id: defaultStudentId || "", occurred_at: todayIso() });
    }
  }, [open, editing, defaultStudentId]);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const handleContactNameChange = (val) => {
    update({ contact_name: val });
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (!val || val.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestLoading(true);
    setShowSuggestions(true);
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await suggestCommContacts(val.trim());
        setSuggestions(res.data || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestLoading(false);
      }
    }, 200);
  };

  const applySuggestion = (s) => {
    update({
      contact_name: s.name,
      contact_role: s.role || form.contact_role,
      contact_phone: s.phone || form.contact_phone,
      contact_email: s.email || form.contact_email,
    });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const addTask = () => {
    update({
      tasks: [
        ...form.tasks,
        { description: "", assignee_id: "", assignee_name: "", due_date: "", reminder_days: 1, status: "open" },
      ],
    });
  };

  const updateTask = (idx, patch) => {
    const next = [...form.tasks];
    next[idx] = { ...next[idx], ...patch };
    if (patch.assignee_id) {
      const s = staff.find((x) => x.id === patch.assignee_id);
      if (s) next[idx].assignee_name = s.name;
    }
    update({ tasks: next });
  };

  const removeTask = (idx) => {
    update({ tasks: form.tasks.filter((_, i) => i !== idx) });
  };

  const addCustomType = async () => {
    const label = newTypeLabel.trim();
    if (!label) return;
    try {
      const res = await createCommunicationType(label);
      toast.success(`Type "${label}" added`);
      setNewTypeLabel("");
      update({ type: res.data.id });
      onSaved && onSaved(true); // signal to refresh types but keep dialog open
    } catch {
      toast.error("Failed to add custom type");
    }
  };

  const handleSave = async () => {
    if (!form.contact_name && !form.subject) {
      toast.error("Add a contact name or subject");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        student_id: form.student_id || null,
        tasks: form.tasks.filter((t) => t.description && t.assignee_id),
        occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : null,
      };
      if (editing) {
        await updateCommunication(editing.id, payload);
        toast.success("Communication updated");
      } else {
        await createCommunication(payload);
        toast.success("Communication logged");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const allTypes = [
    ...Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v })),
    ...(customTypes || []).map((t) => ({ value: t.id, label: t.label })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="comm-dialog-desc">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Communication/Task" : "Log Communication/Task"}</DialogTitle>
          <DialogDescription id="comm-dialog-desc">
            Record a contact, attach optional student, set visibility, and add follow-up tasks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type / Direction / When */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => update({ type: v })}>
                <SelectTrigger data-testid="comm-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allTypes.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1 pt-1">
                <Input
                  placeholder="Add custom type..."
                  value={newTypeLabel}
                  onChange={(e) => setNewTypeLabel(e.target.value)}
                  className="h-7 text-xs"
                  data-testid="comm-form-new-type"
                />
                <Button type="button" size="sm" variant="outline" onClick={addCustomType} className="h-7 px-2 text-xs">+ Add</Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => update({ direction: v })}>
                <SelectTrigger data-testid="comm-form-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incoming">↓ Incoming</SelectItem>
                  <SelectItem value="outgoing">↑ Outgoing</SelectItem>
                  <SelectItem value="task_only">★ Task (self only)</SelectItem>
                  <SelectItem value="reminder">🔔 Reminder (can assign)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>When</Label>
              <Input type="datetime-local" value={form.occurred_at} onChange={(e) => update({ occurred_at: e.target.value })} data-testid="comm-form-when" />
            </div>
          </div>

          {/* Contact */}
          {(form.direction === "task_only" || form.direction === "reminder") && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              {form.direction === "task_only"
                ? "Task mode: contact info optional. The Subject is the headline."
                : "Reminder mode: contact info optional. Assign tasks below to push to specific staff, or leave unassigned for an FYI on the bell."}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1 relative">
              <Label>Contact Name</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => handleContactNameChange(e.target.value)}
                onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                onBlur={() => { setTimeout(() => setShowSuggestions(false), 200); }}
                placeholder="Start typing — we'll search Contacts & past comms"
                data-testid="comm-form-contact-name"
                autoComplete="off"
              />
              {showSuggestions && (suggestions.length > 0 || suggestLoading) && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="comm-form-contact-suggestions">
                  {suggestLoading && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
                  )}
                  {suggestions.map((s, i) => (
                    <button
                      key={`${s.source}-${s.name}-${i}`}
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                      data-testid={`comm-form-suggestion-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{s.name}</span>
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${s.source === "contacts" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {s.source === "contacts" ? "Contact" : "Past comm"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[s.title, s.organization, s.phone, s.email].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label>Contact Role</Label>
              <Select value={form.contact_role} onValueChange={(v) => update({ contact_role: v })}>
                <SelectTrigger data-testid="comm-form-contact-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => update({ contact_phone: e.target.value })} placeholder="555-555-5555" />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input value={form.contact_email} onChange={(e) => update({ contact_email: e.target.value })} placeholder="name@example.com" />
            </div>
          </div>

          {/* Student tag */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Student (optional)</Label>
              <Select value={form.student_id || "none"} onValueChange={(v) => update({ student_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="comm-form-student"><SelectValue placeholder="No student" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No student —</SelectItem>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{(s.preferred_name || s.first_name)} {s.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => update({ visibility: v })}>
                <SelectTrigger data-testid="comm-form-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="only_me">Only Me (admins excluded)</SelectItem>
                  <SelectItem value="private">Private (me + admins)</SelectItem>
                  <SelectItem value="clinical">Clinical Only</SelectItem>
                  <SelectItem value="all">All Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subject + Notes */}
          <div className="space-y-1">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => update({ subject: e.target.value })} placeholder="Brief summary" data-testid="comm-form-subject" />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={form.content}
              onChange={(e) => update({ content: e.target.value })}
              placeholder="Detailed notes from the conversation..."
              rows={5}
              data-testid="comm-form-content"
            />
          </div>

          {/* Tasks */}
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Follow-up Tasks for Staff</Label>
              <Button type="button" size="sm" variant="outline" onClick={addTask} data-testid="comm-form-add-task">+ Add Task</Button>
            </div>
            {form.tasks.length === 0 && (
              <p className="text-xs text-muted-foreground">No tasks yet. Add one to assign action items.</p>
            )}
            {form.tasks.map((t, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-gray-50 p-2 rounded" data-testid={`comm-form-task-${idx}`}>
                <div className="md:col-span-4 space-y-1">
                  <Label className="text-xs">Task</Label>
                  <Input value={t.description} onChange={(e) => updateTask(idx, { description: e.target.value })} placeholder="What needs to be done?" />
                </div>
                <div className="md:col-span-3 space-y-1">
                  <Label className="text-xs">Assignee</Label>
                  <Select value={t.assignee_id || "none"} onValueChange={(v) => updateTask(idx, { assignee_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Choose staff..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Unassigned —</SelectItem>
                      {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Due Date</Label>
                  <Input type="date" value={t.due_date || ""} onChange={(e) => updateTask(idx, { due_date: e.target.value })} />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Remind (days before)</Label>
                  <Input type="number" min="0" max="30" value={t.reminder_days ?? 1} onChange={(e) => updateTask(idx, { reminder_days: parseInt(e.target.value || "1", 10) })} />
                </div>
                <div className="md:col-span-1">
                  <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeTask(idx)} data-testid={`comm-form-remove-task-${idx}`}>✕</Button>
                </div>
              </div>
            ))}
          </div>

          {/* Follow-up for self */}
          <div className="border-t pt-3 space-y-2">
            <Label className="text-sm font-semibold">My Follow-up Reminder <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
            <p className="text-xs text-muted-foreground">
              Leave the date blank if no follow-up is needed — this entry will be saved as a record-only log.
              Set a date to get reminded to circle back yourself (separate from staff tasks above).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Follow-up Date</Label>
                <div className="flex gap-1">
                  <Input type="date" value={form.follow_up_date || ""} onChange={(e) => update({ follow_up_date: e.target.value })} data-testid="comm-form-follow-up-date" />
                  {form.follow_up_date && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => update({ follow_up_date: "", follow_up_notes: "" })} title="Clear follow-up — no follow-up needed" data-testid="comm-form-clear-follow-up">
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Remind (days before)</Label>
                <Input type="number" min="0" max="30" value={form.follow_up_reminder_days ?? 1} disabled={!form.follow_up_date} onChange={(e) => update({ follow_up_reminder_days: parseInt(e.target.value || "1", 10) })} />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Input value={form.follow_up_notes || ""} disabled={!form.follow_up_date} onChange={(e) => update({ follow_up_notes: e.target.value })} placeholder="e.g., Confirm IEP date" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="comm-form-save">
            {saving ? "Saving..." : editing ? "Save Changes" : "Log Communication/Task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
